import Phaser from 'phaser';
import { initAnalytics, track, getIdentity } from './analytics.js';
import { mountFeedbackButton, setFeedbackRoute } from './components/feedbackButton.js';
import { mountBroadcastSlider, setBroadcastSliderVisible } from './components/broadcastSlider.js';
import { mountPlayerDebugOverlay } from './components/playerDebugOverlay.js';
import { createCrapsBridge } from './bridge/crapsBridge.js';
import { createSessionRecorder } from './sessionRecorder.js';
import { initOrientation } from './orientation.js';
import { LobbyBootScene } from './scenes/LobbyBootScene.js';
import { LobbyScene } from './scenes/LobbyScene.js';
import { CrapsTutorialBootScene } from './scenes/craps-tutorial/BootScene.js';
import { CrapsTutorialDiceOverlayScene } from './scenes/craps-tutorial/DiceOverlayScene.js';
import { CrapsTutorialScene } from './scenes/craps-tutorial/TutorialScene.js';
import { CrapsTutorialUIScene } from './scenes/craps-tutorial/UIScene.js';
import { BootScene as CrapsBootScene } from './scenes/craps/BootScene.js';
import { DiceOverlayScene as CrapsDiceOverlayScene } from './scenes/craps/DiceOverlayScene.js';
import { TableScene as CrapsTableScene } from './scenes/craps/TableScene.js';
import { UIScene as CrapsUIScene } from './scenes/craps/UIScene.js';
import { bootWatchingMode } from './spectator/bootWatchingMode.js';
import { load, save, getActiveGame, setActiveGameTurnState } from './persistence.js';

const watchCode = new URLSearchParams(location.search).get('watch');
if (watchCode) {
  void bootWatchingMode(watchCode);
} else {

// Render at physical pixels for hi-DPI sharpness, sized to the natural
// viewport. Portrait viewports get a portrait canvas; TableScene/UIScene
// pick portraitLayout vs landscapeLayout based on canvas dims.
const dpr = window.devicePixelRatio || 1;
const cssW = window.innerWidth;
const cssH = window.innerHeight;
const W = Math.round(cssW * dpr);
const H = Math.round(cssH * dpr);

const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: 'game',
  width: W,
  height: H,
  scale: { mode: Phaser.Scale.NONE, autoCenter: Phaser.Scale.NO_CENTER },
  audio: { noAudio: true },
  scene: [
    LobbyBootScene,
    LobbyScene,
    CrapsBootScene,
    CrapsTableScene,
    CrapsUIScene,
    CrapsDiceOverlayScene,
    CrapsTutorialBootScene,
    CrapsTutorialScene,
    CrapsTutorialUIScene,
    CrapsTutorialDiceOverlayScene,
  ],
});

game.registry.set('dpr', dpr);

initAnalytics({ version: __BUILD_HASH__ });
const sessionRecorder = createSessionRecorder({
  onState: (s) => {
    const p = load();
    if (p.activeGame && p.activeGame.gameId === s.gameId) {
      save(setActiveGameTurnState(p, s.turnId, s.lastSeq));
    }
  },
});
mountFeedbackButton();
setFeedbackRoute('lobby');
mountBroadcastSlider(sessionRecorder);

track('app_loaded', {
  ua: navigator.userAgent,
  viewport_w: window.innerWidth,
  viewport_h: window.innerHeight,
});

window.addEventListener('error', (ev) => {
  track('client_error', {
    message: ev.message ?? 'unknown',
    stack: ev.error instanceof Error ? ev.error.stack ?? '' : '',
    route: location.pathname,
  });
});
window.addEventListener('unhandledrejection', (ev) => {
  const reason = ev.reason;
  track('client_error', {
    message: reason instanceof Error ? reason.message : String(reason ?? 'unhandled rejection'),
    stack: reason instanceof Error ? reason.stack ?? '' : '',
    route: location.pathname,
  });
});

// Debug: expose for in-browser inspection.
(globalThis as unknown as { __game?: Phaser.Game }).__game = game;

const parent = document.getElementById('game');
if (parent) {
  initOrientation({
    game: game as unknown as Parameters<typeof initOrientation>[0]['game'],
    parent,
    getViewport: () => ({ width: window.innerWidth, height: window.innerHeight }),
  });
}

// All scenes the router knows about. `showOnly` stops every scene NOT in the
// target list before starting the targets, so leaving a table and re-entering
// it can't leave stale scenes overlapping.
const ALL_SCENES = [
  'LobbyScene',
  'LobbyBootScene',
  'CrapsBootScene',
  'CrapsTableScene',
  'CrapsUIScene',
  'CrapsDiceOverlayScene',
  'CrapsTutorialBootScene',
  'CrapsTutorialScene',
  'CrapsTutorialUIScene',
  'CrapsTutorialDiceOverlayScene',
];

// Bumped on every navigation. Polling chains capture this on entry and abort
// if the value has moved on by the time they next tick — prevents an old
// tryWire from mounting a bridge against scenes that are no longer current.
let navToken = 0;

let __activeTableId: string | null = null;
let __tableEnteredAt = 0;

function showOnly(game: Phaser.Game, keys: string[]) {
  navToken += 1;
  for (const k of ALL_SCENES) {
    if (!keys.includes(k) && game.scene.isActive(k)) game.scene.stop(k);
  }
  for (const k of keys) game.scene.start(k);
  return navToken;
}

game.events.once('ready', () => {
  wireGlobalNavigation(game);
  const active = getActiveGame(load());
  if (active) {
    // Resume the in-flight game rather than dropping the player on the floor.
    onTableSelected(game, active.tableId);
  } else {
    showOnly(game, ['LobbyScene']);
  }
});

function wireLobbyTaps(game: Phaser.Game) {
  const onLobbyReady = () => {
    const lobby = game.scene.getScene('LobbyScene');
    if (!lobby) {
      setTimeout(onLobbyReady, 50);
      return;
    }
    // Remove any prior listener so re-entering the lobby doesn't stack handlers.
    lobby.events.off('table:tap');
    lobby.events.on('table:tap', (tableId: string) => {
      // Defer the scene-router call out of the lobby's own event-dispatch
      // frame. Phaser absorbs scene.stop() called from inside a scene's
      // event handler, leaving the lobby active under the new game scenes.
      setTimeout(() => onTableSelected(game, tableId), 0);
    });
  };
  onLobbyReady();
}

function wireGlobalNavigation(game: Phaser.Game) {
  wireLobbyTaps(game);

  game.events.on('navigate:lobby', () => {
    if (__activeTableId) {
      track('table_exited', {
        table_id: __activeTableId,
        duration_ms: Date.now() - __tableEnteredAt,
      });
      __activeTableId = null;
    }
    setFeedbackRoute('lobby');
    setBroadcastSliderVisible(false);
    showOnly(game, ['LobbyScene']);
    setTimeout(() => wireLobbyTaps(game), 50);
  });
}

function onTableSelected(game: Phaser.Game, tableId: string) {
  track('lobby_table_clicked', { table_id: tableId });
  __activeTableId = tableId;
  __tableEnteredAt = Date.now();
  track('table_entered', { table_id: tableId });
  setFeedbackRoute(tableId);
  // Share button is craps-only for v1 (recorder only attaches to craps).
  setBroadcastSliderVisible(tableId.startsWith('craps:') && tableId !== 'craps:tutorial');
  if (tableId === 'craps:tutorial') return runCrapsTutorial(game);
  const [gameId, tableKey] = tableId.split(':') as ['craps', 'standard' | 'high-limit' | 'whale'];
  if (gameId === 'craps')     return runCrapsTable(game, tableKey);
}

// Gate dev-only chunks at the import site so non-dev users never fetch them.
// A bare `import()` for every user pays a network round-trip and turns a
// stale-deploy chunk miss into a client_error event, even though the modules
// themselves no-op without these flags. Mirror their checks here.
const isDevPanelEnabled = () =>
  typeof location !== 'undefined' &&
  (location.search.includes('dev') || localStorage.getItem('dev') === '1');
const isShakeTestEnabled = () =>
  typeof location !== 'undefined' &&
  (location.search.includes('shake-test=true') || localStorage.getItem('shake-test') === '1');

function runCrapsTable(
  game: Phaser.Game,
  tableKey: 'standard' | 'high-limit' | 'whale',
) {
  const myToken = showOnly(game, ['CrapsBootScene']);
  waitForScenesCreated(game, ['CrapsBootScene'], myToken).then(() => {
    if (myToken !== navToken) return;
    waitForScenesCreated(game, ['CrapsTableScene', 'CrapsUIScene'], myToken).then(() => {
      if (myToken !== navToken) return;
      const table = game.scene.getScene('CrapsTableScene') as CrapsTableScene;
      const ui = game.scene.getScene('CrapsUIScene') as CrapsUIScene;
      const engine = createCrapsBridge({ table, ui, tableKey, recorder: sessionRecorder, deviceId: getIdentity()?.device_id ?? '' });
      // Producer-side debug overlay (gated behind ?debug). Shows every engine
      // event the recorder is forwarding to the DO, plus the WS handshake state.
      // Mirrors the spectator-side overlay so we can match emitted vs received.
      mountPlayerDebugOverlay(sessionRecorder, engine);
      if (isDevPanelEnabled()) {
        import('./devPanel.js').then(({ mountDevPanel }) => mountDevPanel(engine));
      }
      if (isShakeTestEnabled()) {
        import('./shakeTestPanel.js').then(({ mountShakeTestPanel }) => mountShakeTestPanel());
      }
    });
  });
}

function runCrapsTutorial(game: Phaser.Game) {
  Promise.all([
    import('@casino/craps-engine'),
    import('./scenes/craps-tutorial/tutorialDriver.js'),
    import('./scenes/craps-tutorial/tutorialBridge.js'),
    import('./scenes/craps-tutorial/tutorialScript.js'),
  ]).then(
    ([
      { createEngine, DEFAULT_RULES, ScriptedDice },
      { TutorialDriver },
      { createTutorialBridge },
      { TUTORIAL_SCRIPT },
    ]) => {
      const myToken = showOnly(game, ['CrapsTutorialBootScene']);
      waitForScenesCreated(game, ['CrapsTutorialBootScene'], myToken)
        .then(() =>
          waitForScenesCreated(
            game,
            ['CrapsTutorialScene', 'CrapsTutorialUIScene'],
            myToken,
          ),
        )
        .then(() => {
          if (myToken !== navToken) return;
          const t = game.scene.getScene('CrapsTutorialScene') as CrapsTutorialScene;
          const u = game.scene.getScene('CrapsTutorialUIScene') as CrapsTutorialUIScene;
          const dice = new ScriptedDice();
          const engine = createEngine({
            dice,
            rules: { ...DEFAULT_RULES, tableMin: 500 },
            bankroll: 1_000_00,
          });
          const driver = new TutorialDriver({ engine, dice, script: TUTORIAL_SCRIPT });
          const disposeBridge = createTutorialBridge({
            table: t,
            ui: u,
            engine,
            driver,
            passChipCents: 500,
          });
          t.events.once(Phaser.Scenes.Events.SHUTDOWN, () => disposeBridge());
          u.attachDriver(driver);
        });
    },
  );
}

/**
 * Resolves when each named scene has finished its create() lifecycle.
 *
 * Two cases to handle:
 *  - Scene with preload: SceneManager.start() returns before create() runs
 *    (loader is async). We attach the listener and wait for the CREATE event.
 *  - Scene with no preload: SceneManager.start() runs init→create
 *    synchronously inside the call, so CREATE has already fired by the time
 *    we attach. Detect by checking for status === RUNNING and resolve.
 *
 * Critical for re-entries: Phaser preserves scene class instances, so polling
 * for field truthiness can latch onto destroyed objects from the prior run.
 */
function waitForScenesCreated(game: Phaser.Game, keys: string[], myToken: number): Promise<void> {
  return new Promise<void>((resolve) => {
    let remaining = keys.length;
    const tick = () => {
      if (--remaining === 0) resolve();
    };
    for (const key of keys) {
      const scene = game.scene.getScene(key);
      if (!scene) {
        tick();
        continue;
      }
      const status = scene.sys.settings.status;
      if (status === Phaser.Scenes.RUNNING) {
        tick();
        continue;
      }
      scene.events.once(Phaser.Scenes.Events.CREATE, () => {
        if (myToken !== navToken) return;
        tick();
      });
    }
  });
}

} // end else (play mode)
